import { Emitter } from '../utils/Emitter';

export interface PointerClick {
  /** Normalised device coordinates (-1..1). */
  ndcX: number;
  ndcY: number;
  clientX: number;
  clientY: number;
}

export type KeyCommand = 'act' | 'tool1' | 'tool2' | 'tool3' | 'tool4' | 'escape' | 'chat';

const DRAG_THRESHOLD = 7;

const KEY_COMMANDS: Record<string, KeyCommand> = {
  Space: 'act',
  KeyF: 'act',
  Digit1: 'tool1',
  Digit2: 'tool2',
  Digit3: 'tool3',
  Digit4: 'tool4',
  Escape: 'escape',
  Enter: 'chat',
};

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/**
 * Normalises mouse, touch and keyboard into game-friendly signals: clicks,
 * hover position, camera orbit/zoom deltas and a movement axis.
 */
export class InputManager {
  readonly clicks = new Emitter<PointerClick>();
  readonly commands = new Emitter<KeyCommand>();

  /** Hover position in NDC, or null when the pointer is outside / touch only. */
  hover: { ndcX: number; ndcY: number; clientX: number; clientY: number } | null = null;
  /** Accumulated camera orbit since the last `consumeOrbit`. */
  private orbitX = 0;
  private orbitY = 0;
  private zoomFactor = 1;

  private readonly keys = new Set<string>();
  private readonly pointers = new Map<
    number,
    { x: number; y: number; startX: number; startY: number; dragging: boolean }
  >();
  private pinchDistance = 0;
  private enabled = true;
  private readonly abort = new AbortController();

  constructor(private readonly element: HTMLElement) {
    const opts = { signal: this.abort.signal };
    element.addEventListener('pointerdown', (e) => this.onPointerDown(e), opts);
    window.addEventListener('pointermove', (e) => this.onPointerMove(e), opts);
    window.addEventListener('pointerup', (e) => this.onPointerUp(e), opts);
    window.addEventListener('pointercancel', (e) => this.pointers.delete(e.pointerId), opts);
    element.addEventListener('pointerleave', () => (this.hover = null), opts);
    element.addEventListener('wheel', (e) => this.onWheel(e), { ...opts, passive: false });
    element.addEventListener('contextmenu', (e) => e.preventDefault(), opts);
    window.addEventListener('keydown', (e) => this.onKeyDown(e), opts);
    window.addEventListener('keyup', (e) => this.keys.delete(e.code), opts);
    window.addEventListener('blur', () => this.keys.clear(), opts);
  }

  /** Disables gameplay input (e.g. while a modal is open). Camera still works. */
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    if (!enabled) this.keys.clear();
  }

  /** Movement axis from WASD / arrow keys: x = right, y = forward. */
  moveAxis(): { x: number; y: number } {
    if (!this.enabled) return { x: 0, y: 0 };
    const k = this.keys;
    const x =
      (k.has('KeyD') || k.has('ArrowRight') ? 1 : 0) -
      (k.has('KeyA') || k.has('ArrowLeft') ? 1 : 0);
    const y =
      (k.has('KeyW') || k.has('ArrowUp') ? 1 : 0) - (k.has('KeyS') || k.has('ArrowDown') ? 1 : 0);
    const length = Math.hypot(x, y) || 1;
    return { x: x / length, y: y / length };
  }

  /** Keyboard camera orbit (Q / E). */
  keyOrbit(): number {
    if (!this.enabled) return 0;
    return (this.keys.has('KeyE') ? 1 : 0) - (this.keys.has('KeyQ') ? 1 : 0);
  }

  consumeOrbit(): { x: number; y: number; zoom: number } {
    const out = { x: this.orbitX, y: this.orbitY, zoom: this.zoomFactor };
    this.orbitX = this.orbitY = 0;
    this.zoomFactor = 1;
    return out;
  }

  dispose(): void {
    this.abort.abort();
    this.clicks.clear();
    this.commands.clear();
  }

  private toNdc(clientX: number, clientY: number) {
    const rect = this.element.getBoundingClientRect();
    return {
      ndcX: ((clientX - rect.left) / rect.width) * 2 - 1,
      ndcY: -((clientY - rect.top) / rect.height) * 2 + 1,
      clientX,
      clientY,
    };
  }

  private onPointerDown(e: PointerEvent): void {
    this.pointers.set(e.pointerId, {
      x: e.clientX,
      y: e.clientY,
      startX: e.clientX,
      startY: e.clientY,
      dragging: false,
    });
    if (this.pointers.size === 2) this.pinchDistance = this.currentPinch();
  }

  private onPointerMove(e: PointerEvent): void {
    if (e.pointerType === 'mouse' && e.target === this.element)
      this.hover = this.toNdc(e.clientX, e.clientY);
    const pointer = this.pointers.get(e.pointerId);
    if (!pointer) return;
    const dx = e.clientX - pointer.x;
    const dy = e.clientY - pointer.y;
    pointer.x = e.clientX;
    pointer.y = e.clientY;
    if (
      !pointer.dragging &&
      Math.hypot(e.clientX - pointer.startX, e.clientY - pointer.startY) > DRAG_THRESHOLD
    ) {
      pointer.dragging = true;
    }
    if (this.pointers.size === 2) {
      const distance = this.currentPinch();
      if (this.pinchDistance > 0 && distance > 0) this.zoomFactor *= this.pinchDistance / distance;
      this.pinchDistance = distance;
      pointer.dragging = true;
      return;
    }
    if (pointer.dragging) {
      this.orbitX -= dx * 0.006;
      this.orbitY -= dy * 0.004;
    }
  }

  private onPointerUp(e: PointerEvent): void {
    const pointer = this.pointers.get(e.pointerId);
    this.pointers.delete(e.pointerId);
    if (!pointer || pointer.dragging || this.pointers.size > 0) return;
    if (e.button !== 0 && e.pointerType === 'mouse') return;
    if (e.target !== this.element) return;
    if (!this.enabled) return;
    this.clicks.emit(this.toNdc(e.clientX, e.clientY));
  }

  private onWheel(e: WheelEvent): void {
    e.preventDefault();
    this.zoomFactor *= Math.exp(e.deltaY * 0.0012);
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (isTypingTarget(e.target)) return;
    const command = KEY_COMMANDS[e.code];
    if (command === 'escape' || command === 'chat') {
      this.commands.emit(command);
      return;
    }
    if (!this.enabled) return;
    this.keys.add(e.code);
    if (command && !e.repeat) {
      if (command === 'act') e.preventDefault();
      this.commands.emit(command);
    }
  }

  private currentPinch(): number {
    const [a, b] = [...this.pointers.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }
}
